<template>
  <CustomTable
    :columns="columnsTableFields"
    :data="data"
    :is-loading="isLoading"
    :show-checkbox-row="false"
    :sort-direction="'asc'"
    :skeleton-row-number="1"
    :custom-class="'table-share-profile-progress !tw-shadow-none'"
    :header-wrapper-class="'tw-py-[20px] tw-px-[25px]'"
  >
    <template #cell(date)="value">
      <span
        v-if="value.item.checklistHistoryDate"
        class="tw-text-[13px] tw-text-[#231F20] tw-font-medium tw-whitespace-nowrap"
      >
        {{ formatDate(value.item.checklistHistoryDate) }}
      </span>
    </template>

    <template #cell(action)="value">
      <span class="tw-text-[13px] tw-text-[#231F20] tw-whitespace-nowrap">
        {{ value.item.checklistHistoryName }}
      </span>
    </template>

    <template #cell(scoreImpact)="value">
      <span class="tw-text-[13px] tw-text-[#231F20] tw-whitespace-nowrap">
        {{ parseFloat(value.item.checklistHistoryScore) }}%
        <span v-if="value.idx !== data.length - 1"
          >(<span
            :class="`${
              value.item.checklistHistoryImprovementScore > 0
                ? 'tw-text-[#01893d]'
                : 'tw-text-[#F7553c]'
            }`"
          >
            {{
              (value.item.checklistHistoryImprovementScore > 0 ? "+" : "") +
              value.item.checklistHistoryImprovementScore +
              "%"
            }} </span
          >)
        </span>
      </span>
    </template>
    <template #cell(notes)="value">
      <div class="tw-flex tw-gap-[10px]">
        <TextAreaRoadmap
          :key="`roadmap-how-${value.item.checklistId}`"
          :is-edit-status="isShowEdit"
          :item="value.item"
          :key-field="'checklistHistoryNotes'"
          :item-invalids="itemInvalids"
          @edit-notes="
            (text: string) => updateNotes(text, value.item.checklistId)
          "
          @update-notes="
            (text: string) => {
              value.item.checklistHistoryNotes = text;
            }
          "
        />
      </div>
    </template>
  </CustomTable>
</template>
<script lang="ts" setup>
import { getComponentUiText } from "@/utils/utilities";
import CustomTable, {
  CustomTableColumn,
} from "@/components/common/customTable.vue";
import { computed, ref, watch } from "vue";
import { IChecklistHistoryReportChecklistDetail } from "@/interfaces/shares";
import { convertUTCTimeToLocalTime } from "@/utils/dateTimeUtils";
import TextAreaRoadmap from "./textAreaRoadmap.vue";

const props = defineProps({
  data: {
    type: Array as () => IChecklistHistoryReportChecklistDetail[],
    required: true,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    required: false,
  },
  isShowEdit: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  columns: {
    type: Array,
    required: false,
    default: () => ["date", "action", "scoreImpact", "notes"],
  },
});
const emits = defineEmits(["edit-notes", "on-form-invalid"]);

const columnsTableFields = computed<CustomTableColumn[]>(() => {
  return [
    {
      text: getComponentUiText(
        "app-report-achievements-and-progress-table-date-header-title-1",
      ),
      value: "date",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-medium",
      sortable: false,
      sortDefault: false,
    },
    {
      text: getComponentUiText(
        "app-report-achievements-and-progress-table-action-header-title-1",
      ),
      value: "action",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-normal",
    },
    {
      text: getComponentUiText(
        "app-report-achievements-and-progress-table-score-impact-header-title-1",
      ),
      value: "scoreImpact",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-normal",
    },
    {
      text: getComponentUiText(
        "app-report-achievements-and-progress-table-notes-header-title-1",
      ),
      value: "notes",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase tw-w-[58%]",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-normal tw-w-[58%]",
    },
  ].filter((item) => props.columns.includes(item.value));
});

const itemInvalids: any = ref([]);
const updateNotes = (value: string, checklistId) => {
  const item = props.data.find((item: any) => item.checklistId === checklistId);
  if (!item) return;
  const idxInvalid = itemInvalids.value.findIndex(
    (item: any) => item === checklistId,
  );
  if (value && idxInvalid >= 0) {
    itemInvalids.value.splice(idxInvalid, 1);
  } else if (!value && idxInvalid === -1) {
    itemInvalids.value.push(checklistId);
  }
};

watch(
  () => itemInvalids.value,
  (newVal) => {
    if (itemInvalids.value.length > 0) {
      emits("on-form-invalid", true);
    } else {
      emits("on-form-invalid", false);
    }
  },
  { deep: true },
);
const formatDate = (date: string) => {
  return convertUTCTimeToLocalTime(date);
};
</script>
<style lang="scss" scoped>
:deep(.table-header) {
  height: 56px !important;
  background-color: #211f58 !important;
}
.input-notes {
  background-color: rgba(239, 240, 241, 0.5);
}
:deep(code) {
  background: #c7c7c780;
  padding: 2px;
}

:deep(*) {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:deep(ol) {
  padding-left: 25px;
}

:deep(ul) {
  padding-left: 25px;
}
</style>
